import { RouteRecordRaw } from 'vue-router';
import { CommonRoutesName } from '../constants/routesName';
import { getDefaultRouteForUser } from '../guards/authGuard';
import { useUserStore } from '@/common/stores/userStore';
import LoginPage from '@/features/common/auth/views/LoginPage.vue';
import { ROUTE_CATEGORY, UserRole } from '../types';
import { StudentRouteName } from '../constants/routesName';
import { tr } from 'date-fns/locale';
const ConfigTestPage = () => import('@/views/ConfigTestPage.vue');
const ErrorModalTestPage = () => import('@/views/ErrorModalTestPage.vue');
const NoRolePage = () => import('@/features/homepage/views/NoRolePage.vue');
const NotAllowedPage = () => import('@/features/common/auth/views/NotAllowedPage.vue');
const SettingsPage = () => import('@/features/settings/views/SettingsPage.vue');
const NewsPage = () => import('@/features/news/views/NewsPage.vue');
const NewsDetailPage = () => import('@/features/news/views/NewsDetailPage.vue');
const NotFoundPage = () => import('@/views/NotFoundPage.vue');
const PagesMetiersPage = () => import('@/features/pages-metiers/views/PagesMetiers.vue');
const ProfilePage = () => import('@/features/profile/views/Profile.vue');
const StoryblokPage = () => import('@/features/storyblok/views/StoryblokPage.vue');
const StoryblokSlugListPage = () => import('@/features/storyblok/views/StoryblokSlugListPage.vue');
const PartnershipUniversitiesMap = () =>
  import('@/features/partnership-universities/views/PartnershipUniversitiesMap.vue');

/**
 * Routes communes accessibles à tous les utilisateurs
 */
export const commonRoutes: RouteRecordRaw[] = [
  // ROUTES DE REDIRECTION
  {
    path: '/',
    redirect: '/home',
  },
  {
    path: '/login',
    name: CommonRoutesName.Login,
    component: LoginPage,
    meta: {
      title: 'routes.login',
      hideInMenu: true,
      category: ROUTE_CATEGORY.COMMON,
    },
  },
  {
    path: '/not-allowed',
    name: CommonRoutesName.NotAllowed,
    component: NotAllowedPage,
    meta: {
      title: 'routes.notAllowed',
      hideInMenu: true,
      category: ROUTE_CATEGORY.COMMON,
    },
  },
  {
    path: '/home',
    name: CommonRoutesName.Home,
    component: NoRolePage,
    beforeEnter: () => {
      // If the user has a role, redirect to their role-specific homepage
      const userStore = useUserStore();
      const roleRoute = getDefaultRouteForUser(userStore.userRoles);
      if (roleRoute !== '/') {
        return roleRoute;
      }
      // No role → stay on NoRolePage
    },
    meta: {
      title: 'routes.home',
      icon: 'home-outline',
      category: ROUTE_CATEGORY.COMMON,
    },
  },
  // ROUTES ACCESSIBLES AUX UTILISATEURS AYANT UN ROLE
  {
    path: '/business/:id',
    name: CommonRoutesName.Business,
    component: PagesMetiersPage,
    meta: {
      title: 'routes.business',
      requiresRoles: [UserRole.STUDENT, UserRole.COLLABORATEUR, UserRole.ADMINISTRATOR],
      category: ROUTE_CATEGORY.COMMON,
      hideInMenu: true,
    },
  },
  {
    path: '/myprofile',
    name: CommonRoutesName.MyProfile,
    component: ProfilePage,
    meta: {
      requiresRoles: [UserRole.STUDENT, UserRole.COLLABORATEUR, UserRole.ADMINISTRATOR],
      title: 'routes.myProfile',
      icon: 'person-circle-outline',
      category: ROUTE_CATEGORY.COMMON,
    },
  },
  {
    path: '/news',
    name: StudentRouteName.News,
    component: NewsPage,
    meta: {
      requiresRoles: [UserRole.STUDENT, UserRole.COLLABORATEUR, UserRole.ADMINISTRATOR],
      title: 'routes.news',
      icon: 'newspaper-outline',
      category: ROUTE_CATEGORY.STUDENT,
    },
  },
  {
    path: '/news/:id',
    name: StudentRouteName.NewsDetail,
    component: NewsDetailPage,
    meta: {
      requiresRoles: [UserRole.STUDENT, UserRole.COLLABORATEUR, UserRole.ADMINISTRATOR],
      title: 'routes.newsDetail',
      hideInMenu: true,
      category: ROUTE_CATEGORY.STUDENT,
    },
  },
  {
    path: '/profile/:mail',
    name: CommonRoutesName.Profile,
    component: ProfilePage,
    meta: {
      title: 'routes.profile',
      requiresRoles: [UserRole.STUDENT, UserRole.COLLABORATEUR, UserRole.ADMINISTRATOR],
      category: ROUTE_CATEGORY.COMMON,
      hideInMenu: false,
    },
  },
  {
    path: '/settings',
    name: CommonRoutesName.Settings,
    component: SettingsPage,
    meta: {
      requiresRoles: [UserRole.STUDENT, UserRole.COLLABORATEUR, UserRole.ADMINISTRATOR],
      title: 'routes.settings',
      icon: 'cog-outline',
      category: ROUTE_CATEGORY.COMMON,
    },
  },
  {
    path: '/storyblok-slugs',
    name: CommonRoutesName.StoryblokSlugList,
    component: StoryblokSlugListPage,
    meta: {
      requiresRoles: [UserRole.STUDENT, UserRole.COLLABORATEUR, UserRole.ADMINISTRATOR],
      title: 'routes.storyblokSlugList',
      icon: 'list-outline',
      category: ROUTE_CATEGORY.COLLABORATEUR,
    },
  },
  {
    path: '/storyblok-page',
    name: CommonRoutesName.StoryblokPage,
    component: StoryblokPage,
    meta: {
      requiresRoles: [UserRole.STUDENT, UserRole.COLLABORATEUR, UserRole.ADMINISTRATOR],
      title: 'routes.storyblokPage',
      icon: 'eye-outline',
      category: ROUTE_CATEGORY.COLLABORATEUR,
      hideInMenu: true,
    },
  },
  // {
  //   path: '/storyblok-preview',
  //   name: CommonRoutesName.StoryblokPage,
  //   component: StoryblokPage,
  //   meta: {
  //     requiresRoles: [UserRole.STUDENT, UserRole.COLLABORATEUR, UserRole.ADMINISTRATOR],
  //     title: 'routes.storyblokPage',
  //     icon: 'eye-outline',
  //     category: ROUTE_CATEGORY.COLLABORATEUR,
  //     hideInMenu: true,
  //   },
  // },
  {
    path: '/config-test',
    name: CommonRoutesName.ConfigTest,
    component: ConfigTestPage,
    meta: {
      title: 'routes.configTest',
      icon: 'settings-outline',
      category: ROUTE_CATEGORY.COMMON,
    },
  },
  {
    path: '/error-modal-test',
    name: 'ErrorModalTest',
    component: ErrorModalTestPage,
    meta: {
      title: 'Error Modal Test',
      icon: 'warning-outline',
      category: ROUTE_CATEGORY.COMMON,
      hideInMenu: true,
    },
  },
  {
    path: '/:pathMatch(.*)*',
    name: CommonRoutesName.NotFound,
    component: NotFoundPage,
    meta: {
      title: 'routes.notFound',
      hideInMenu: true,
      category: ROUTE_CATEGORY.COMMON,
    },
  },
  // {
  //   path: '/search',
  //   name: CommonRoutesName.SearchUsers,
  //   component: () => import('@/features/search/views/SearchPage.vue'),
  //   meta: {
  //     title: 'routes.search',
  //     requiresRoles: [UserRole.STUDENT, UserRole.COLLABORATEUR, UserRole.ADMINISTRATOR],
  //     category: ROUTE_CATEGORY.COMMON,
  //     hideInMenu: false,
  //   },
  // },
  {
    path: '/profile/:mail',
    name: CommonRoutesName.Profile,
    component: ProfilePage,
    meta: {
      title: 'routes.profile',
      requiresRoles: [UserRole.STUDENT, UserRole.COLLABORATEUR, UserRole.ADMINISTRATOR],
      category: ROUTE_CATEGORY.COMMON,
      hideInMenu: false,
    },
  },
  {
    path: '/partnership-universities',
    name: CommonRoutesName.PartnershipUniversities,
    component: PartnershipUniversitiesMap,
    meta: {
      requiresRoles: [UserRole.STUDENT, UserRole.COLLABORATEUR, UserRole.ADMINISTRATOR],
      title: 'routes.partnershipUniversities',
      icon: 'shield-outline',
      category: ROUTE_CATEGORY.COMMON,
      hideInMenu: true,
    },
  },
];
